import * as stylex from '@stylexjs/stylex'
import { type ForwardedRef, type MouseEvent, type MouseEventHandler, type ReactNode } from 'react'
import { colors } from '../tokens/colors.stylex'
import { typography } from '../tokens/typography.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const baseStyles = stylex.create({
  root: {
    padding: 8,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: {
      'default': colors.white,
      '@media (hover: hover)': {
        'default': null,
        ':hover': colors.grey100,
      },
    },
    boxSizing: 'border-box',
    color: colors.grey800,
    cursor: 'pointer',
    flexShrink: 0,
    fontFamily: typography.font,
    fontSize: typography.sizeBody,
    fontWeight: 400,
    textOverflow: 'ellipsis',
    minWidth: '80px',
  },
  selected: {
    backgroundColor: colors.grey100,
  },
  highlighted: {
    backgroundColor: colors.blueGrey100,
  },
  disabled: {
    backgroundColor: colors.white,
    color: colors.grey300,
    cursor: 'not-allowed',
  },
})

export type SelectOptionValue = string | number | string[] | null

export interface SelectOptionProps<Value extends SelectOptionValue = SelectOptionValue> {
  /**
   * 覆寫各狀態樣式（root / selected / highlighted / disabled）
   */
  xStyles?: Partial<XStyles<keyof typeof baseStyles>>
  /**
   * 選項的值
   */
  value: Value
  /**
   * 是否禁用此選項
   */
  disabled?: boolean
  /**
   * 是否為已選取狀態
   */
  selected?: boolean
  /**
   * 視覺反映 keyboard / hover 當下的焦點選項。純 visual flag，不寫任何 DOM aria；
   * combobox 的 ARIA 對應走 input 的 aria-activedescendant，由 hook 注入。
   */
  highlighted?: boolean
  /**
   * 選項顯示內容
   */
  children?: ReactNode
  /**
   * AutoComplete 用來當 inputValue 與搜尋比對的文字。children 帶非文字內容
   * （例如標記商品屬性的 chip）時給這個，否則那些字會被一起抽進輸入框。
   * 沒給就沿用從 children 遞迴抽出的文字。
   */
  displayText?: string
  /**
   * 點擊選項時的回呼
   */
  onClick?: (e: MouseEvent<HTMLDivElement>, newValue: Value) => void
  /**
   * 根元素的 ref
   */
  rootRef?: ForwardedRef<HTMLDivElement>
  /**
   * DOM id（給 AutoComplete 的 aria-activedescendant 指向）
   */
  id?: string
  /**
   * ARIA role（給 AutoComplete 注入 'option'）
   */
  role?: string
  /**
   * ARIA selected（給 AutoComplete 注入）
   */
  'aria-selected'?: boolean
  /**
   * ARIA disabled（給 AutoComplete 注入）
   */
  'aria-disabled'?: boolean
  /**
   * mouse move handler（給 AutoComplete 在 hover 時更新 highlightedIndex）
   */
  onMouseMove?: MouseEventHandler<HTMLDivElement>
  /**
   * tabIndex 覆寫（給 AutoComplete combobox pattern 注入 -1，避免 Tab 偷走 input 的 focus；
   * Select 場景用預設 0 保留鍵盤可達性）
   */
  tabIndex?: number
}

/**
 * SelectOption 下拉選單的選項元件。
 * 通常搭配 Select 或 AutoComplete 使用。
 *
 * @example
 * ```tsx
 * <SelectOption value="dog">狗</SelectOption>
 * ```
 */
export const SelectOption = <Value extends SelectOptionValue>(props: SelectOptionProps<Value>) => {
  // displayText 只給 AutoComplete 讀，不該落到 DOM 上
  const { value, disabled = false, selected, highlighted, children, displayText: _displayText, onClick, rootRef, xStyles, ...rest } = props
  const styles = useXStyles(baseStyles, xStyles)

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) return
    onClick?.(e, value)
  }

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      {...stylex.props(
        styles.root,
        selected && styles.selected,
        highlighted && styles.highlighted,
        disabled && styles.disabled,
      )}
      onClick={handleClick}
      data-testid={`select-option-${value}`}
      {...rest}
    >
      {children}
    </div>
  )
}
